/**
 * OpsTiles — three small admin tiles on the Session Digests page:
 *   1. LLM Retry counter (24h transient errors per agent)
 *   2. Wizard Telemetry (viewed / seeded / skipped / no_action in last 7d)
 *   3. Dedupe rolling rate (skipped writes per digest, 24h average)
 *
 * Pure-data tiles — no actions. The action affordance ("Reset user edits")
 * lives per-row in the main digest table.
 */
import { useEffect, useState } from "react";
import { authAxios, getAccessToken } from "../../utils/auth";
import {
  RefreshCw,
  Wand2,
  GitCompare,
  Database,
  Download,
} from "lucide-react";
import { toast } from "sonner";

function Tile({ icon: Icon, title, value, subtitle, tone, testid }) {
  const tones = {
    neutral: "border-divine-gold/20 bg-divine-charcoal text-divine-cream",
    warn: "border-amber-500/30 bg-amber-500/5 text-amber-200",
    error: "border-rose-500/30 bg-rose-500/5 text-rose-200",
    good: "border-emerald-500/30 bg-emerald-500/5 text-emerald-200",
  };
  return (
    <div
      data-testid={testid}
      className={`rounded-lg border p-3 ${tones[tone] || tones.neutral}`}
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider opacity-80">
        <Icon className="w-3.5 h-3.5" />
        {title}
      </div>
      <div className="text-2xl font-semibold mt-1 tabular-nums">{value}</div>
      {subtitle ? (
        <div className="text-[11px] opacity-70 mt-0.5">{subtitle}</div>
      ) : null}
    </div>
  );
}

export default function OpsTiles({ sessions = [] }) {
  const [retryStats, setRetryStats] = useState(null);
  const [wizardStats, setWizardStats] = useState(null);
  // Pre-flight info for the database backup tile. We only show counts; the
  // user clicks the button to actually stream the zip via authenticated fetch.
  const [backupPreview, setBackupPreview] = useState(null);
  const [downloadingBackup, setDownloadingBackup] = useState(false);

  // Fetch (or re-fetch) just the backup preview. Used on mount and after a
  // successful download so the "Last backup: N ago" line stays fresh without
  // reloading the whole page.
  const refreshBackupPreview = async () => {
    try {
      const r = await authAxios.get("/admin/backup/preview");
      setBackupPreview(r.data);
    } catch (e) {
      console.error("backup preview fetch failed", e);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [r1, r2, r3] = await Promise.all([
          authAxios.get("/admin/ops/llm-retry-stats"),
          authAxios.get("/admin/ops/wizard-telemetry"),
          authAxios.get("/admin/backup/preview"),
        ]);
        if (cancelled) return;
        setRetryStats(r1.data);
        setWizardStats(r2.data);
        setBackupPreview(r3.data);
      } catch (e) {
        console.error("ops tiles fetch failed", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Download the full DB as a single zip. We can't just navigate to the URL
  // because the browser address bar doesn't attach the Authorization header
  // — so we fetch with the token, convert to a Blob, and trigger a synthetic
  // anchor click. Same idea as a server-side Content-Disposition response,
  // but driven by JS so auth flows through correctly.
  const handleDownloadBackup = async () => {
    if (downloadingBackup) return;
    const token = getAccessToken();
    if (!token) {
      toast.error("Not authenticated — log in again");
      return;
    }
    setDownloadingBackup(true);
    const tid = toast.loading("Preparing backup zip…");
    try {
      const base = process.env.REACT_APP_BACKEND_URL || "";
      const res = await fetch(`${base}/api/admin/backup`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        throw new Error(`Backup failed: ${res.status} ${res.statusText}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const ts = new Date()
        .toISOString()
        .replace(/[:.]/g, "-")
        .slice(0, 19);
      const a = document.createElement("a");
      a.href = url;
      a.download = `agent-eros-backup-${ts}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Backup downloaded", { id: tid });
      // Refresh the preview so the "Last backup" indicator reflects this run.
      refreshBackupPreview();
    } catch (e) {
      console.error("backup download failed", e);
      toast.error(e?.message || "Backup download failed", { id: tid });
    } finally {
      setDownloadingBackup(false);
    }
  };

  const totalRetries = retryStats?.total_retries_in_window ?? "—";
  const giveUps = retryStats?.total_give_ups_all_time ?? 0;
  const retryTone = giveUps > 0 ? "error" : totalRetries > 0 ? "warn" : "neutral";

  const viewed = wizardStats?.viewed ?? 0;
  const seeded = wizardStats?.seeded ?? 0;
  const skipped = wizardStats?.skipped ?? 0;
  const noAction = wizardStats?.no_action ?? 0;
  const conv = wizardStats?.conversion_rate;
  const wizardSubtitle =
    viewed > 0
      ? `${seeded} seeded · ${skipped} skipped · ${noAction} ignored` +
        (conv !== null && conv !== undefined
          ? ` · ${(conv * 100).toFixed(0)}% acted`
          : "")
      : "No wizard activity in the last 7 days";

  // Dedupe rolling average across loaded session rows. We count only the rows
  // that actually have a latest_digest with a numeric dedupe value (rows with
  // no digest yet are excluded from the average so they don't drag it to 0).
  const dedupeStats = (() => {
    const values = sessions
      .map((s) => s?.latest_digest?.routing_results?.dedupe_summary?.total_dedupes)
      .filter((v) => typeof v === "number");
    if (values.length === 0) return { avg: null, count: 0, max: null };
    const sum = values.reduce((a, b) => a + b, 0);
    return {
      avg: sum / values.length,
      count: values.length,
      max: Math.max(...values),
    };
  })();
  const dedupeAvgDisplay =
    dedupeStats.avg == null ? "—" : dedupeStats.avg.toFixed(1);
  const dedupeTone =
    dedupeStats.avg == null
      ? "neutral"
      : dedupeStats.avg > 10
        ? "error"
        : dedupeStats.avg > 3
          ? "warn"
          : "good";
  const dedupeSubtitle =
    dedupeStats.count === 0
      ? "No digests loaded yet"
      : `Across ${dedupeStats.count} digest${dedupeStats.count === 1 ? "" : "s"} · max ${dedupeStats.max}`;

  // "Last backup: 2 hours ago" — compact relative time formatter. Returns
  // null when we have no recorded backup yet (renders as "never" in the UI).
  const formatLastBackup = (iso) => {
    if (!iso) return null;
    const then = new Date(iso).getTime();
    if (!Number.isFinite(then)) return null;
    const diffSec = Math.max(0, (Date.now() - then) / 1000);
    if (diffSec < 60) return "just now";
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
    if (diffSec < 86400 * 30) return `${Math.floor(diffSec / 86400)}d ago`;
    return new Date(iso).toLocaleDateString();
  };
  const lastBackupLabel = formatLastBackup(backupPreview?.last_backup_at);

  return (
    <section
      data-testid="ops-tiles"
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3"
    >
      <Tile
        icon={RefreshCw}
        title="LLM retries (24h)"
        value={totalRetries}
        subtitle={
          giveUps > 0
            ? `${giveUps} give-up${giveUps === 1 ? "" : "s"} all-time`
            : "no give-ups all-time"
        }
        tone={retryTone}
        testid="ops-tile-llm-retry"
      />
      <Tile
        icon={Wand2}
        title="Wizard activity (7d)"
        value={viewed}
        subtitle={wizardSubtitle}
        tone="neutral"
        testid="ops-tile-wizard"
      />
      <Tile
        icon={GitCompare}
        title="Dedupe (per digest avg)"
        value={dedupeAvgDisplay}
        subtitle={dedupeSubtitle}
        tone={dedupeTone}
        testid="ops-tile-dedupe"
      />
      {/* Database Backup tile — pre-flight info + one-click authenticated zip
          download. Acts as the visible affordance for the
          GET /api/admin/backup endpoint that otherwise can't be invoked from
          the browser address bar (no Authorization header). */}
      <div
        data-testid="ops-tile-backup"
        className="rounded-lg border border-divine-gold/20 bg-divine-charcoal text-divine-cream p-3 flex flex-col"
      >
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider opacity-80">
          <Database className="w-3.5 h-3.5" />
          Database Backup
        </div>
        <div className="text-2xl font-semibold mt-1 tabular-nums">
          {backupPreview?.total_docs_estimate?.toLocaleString() ?? "—"}
        </div>
        <div className="text-[11px] opacity-70 mt-0.5">
          {backupPreview
            ? `${backupPreview.collections.filter((c) => !c.excluded_by_default).length} collections in ${backupPreview.database}`
            : "Loading preview…"}
        </div>
        <div
          className="text-[11px] opacity-60 mt-1 mb-2"
          data-testid="ops-tile-backup-last"
          title={
            backupPreview?.last_backup_at
              ? `${backupPreview.last_backup_at}${backupPreview.last_backup_by_email ? ` · by ${backupPreview.last_backup_by_email}` : ""}`
              : "No backup recorded yet"
          }
        >
          {backupPreview
            ? lastBackupLabel
              ? `Last backup · ${lastBackupLabel}`
              : "Never backed up"
            : "\u00A0"}
        </div>
        {/* In-app download is temporarily disabled on production: building a
            24 MB zip inside the request lifecycle exceeds the prod pod's
            memory ceiling and OOM-kills the worker, taking the whole app
            down for ~30-90 s. Per-collection streaming (CHANGELOG n) reduced
            peak RAM ~10x but the prod pod is still too constrained.
            Backlog: replace with a background task + object storage flow
            (CHANGELOG o, "Option B"). Until then, use mongodump via support
            or the JS console snippet on a healthy pod. */}
        <button
          type="button"
          disabled
          className="mt-auto inline-flex items-center justify-center gap-1.5 rounded-md border border-amber-400/40 bg-amber-400/10 px-2.5 py-1.5 text-xs text-amber-200 font-medium opacity-70 cursor-not-allowed"
          data-testid="ops-tile-backup-download"
          title="Disabled on production until Option B ships — backup zip build OOM-kills the prod pod. Use mongodump via Emergent Support."
        >
          <Download className="w-3.5 h-3.5" />
          Disabled · backlog
        </button>
        <div className="text-[10px] opacity-55 mt-1.5 leading-snug">
          Crashes prod pod. Awaiting background-task rewrite. Ask Emergent Support for a <code className="text-amber-200/80">mongodump</code> until then.
        </div>
      </div>
    </section>
  );
}
